/* NYLA Makeup Engine — WebGL renderer for photoreal matte makeup.
 *
 * Renders the camera/photo + makeup in a 3-stage GPU pipeline:
 *   1) MASK pass (half res FBO):  R = lip coverage (smoothed annulus mesh between
 *      the outer & inner lip contours — the mouth interior is never painted),
 *      G = blush coverage (analytic soft ellipses on the cheekbones),
 *      B = scene luminance (used as the local-average light map).
 *   2) BLUR pass (separable gaussian): feathers the lip edge and widens the
 *      luminance map so it represents the *neighbourhood* average light.
 *   3) COMPOSITE pass (full res): re-lights the shade colour with the lip's own
 *      micro-texture:  ratio = pixelLuma / localAvgLuma  → creases keep their
 *      depth, specular shine is crushed (matte), and the shade hue stays true
 *      on every skin tone (no muddy multiply).
 *
 * The engine is UI-agnostic: feed it a <video>/<img> + face landmarks each
 * frame and it draws. Exposed as window.NylaMakeup.Engine (no build step).
 */
(function () {
  'use strict';

  // MediaPipe FaceMesh lip contours — ordered closed loops, index-aligned
  // (outer[i] sits opposite inner[i]; both start at the left corner).
  var LIPS_OUTER = [61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146];
  var LIPS_INNER = [78, 191, 80, 81, 82, 13, 312, 311, 310, 415, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95];
  var CHEEK_A = [50, 101, 118, 117];     // cheek apple cluster (one side)
  var CHEEK_B = [280, 330, 347, 346];    // other side
  var SIDE_A = 234, SIDE_B = 454;        // face-oval points by the ears
  var UP_TOP = 168, UP_BOT = 152;        // nose bridge → chin = face "up" axis

  // Where the scene is measured. Skin: upper cheeks and forehead — clear of a
  // beard, a moustache and the lips themselves. Lips: half-way between the
  // outer and inner contour, i.e. the middle of the lip flesh, corners excluded.
  var SKIN_PTS = [50, 280, 101, 330, 151, 108, 337, 9, 117, 346];
  var LIP_MID = [2, 3, 4, 6, 7, 8, 12, 13, 14, 16, 17, 18];
  // Average skin colour in neutral light (linear). Only its HUE is used, to
  // tell how warm or cool the room's light is; skin lightness varies by person.
  var SKIN_PRIOR = [0.55, 0.33, 0.24];

  // Cheek patch of MediaPipe's canonical face mesh: landmark ids, their UV on the
  // canonical face map, and the triangles between them (local indices).
  var CHEEK_LM = [7, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 33, 34, 35, 36, 50, 56, 92, 93, 100, 101, 110, 111, 113, 116, 117, 118, 119, 120, 121, 123, 124, 127, 129, 130, 132, 137, 139, 142, 143, 144, 145, 147, 153, 154, 156, 157, 158, 159, 160, 161, 163, 177, 186, 187, 203, 205, 206, 207, 213, 216, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 232, 234, 246, 247, 249, 252, 253, 254, 255, 256, 257, 258, 259, 260, 261, 263, 264, 265, 266, 280, 286, 322, 323, 329, 330, 339, 340, 342, 345, 346, 347, 348, 349, 350, 352, 353, 356, 358, 359, 361, 366, 368, 371, 372, 373, 374, 376, 380, 381, 383, 384, 385, 386, 387, 388, 390, 401, 410, 411, 423, 425, 426, 427, 433, 436, 442, 443, 444, 445, 446, 447, 448, 449, 450, 451, 452, 454, 466, 467];
  var CHEEK_UV = [0.2897, 0.6192, 0.3659, 0.5904, 0.3388, 0.587, 0.3111, 0.5905, 0.2747, 0.6109, 0.3934, 0.5963, 0.3452, 0.656, 0.3701, 0.6539, 0.3193, 0.6527, 0.2979, 0.6464, 0.2478, 0.5892, 0.2801, 0.6244, 0.1063, 0.6, 0.2099, 0.6086, 0.3558, 0.4656, 0.2769, 0.4679, 0.3924, 0.6461, 0.3901, 0.3604, 0.014, 0.44, 0.3721, 0.5266, 0.3346, 0.5039, 0.289, 0.601, 0.2189, 0.5646, 0.2571, 0.6446, 0.1786, 0.5424, 0.2473, 0.5428, 0.2863, 0.5323, 0.3328, 0.5393, 0.3688, 0.5528, 0.399, 0.5673, 0.1892, 0.4761, 0.229, 0.651, 0.0195, 0.5984, 0.397, 0.4512, 0.2665, 0.623, 0.0323, 0.3556, 0.1076, 0.4592, 0.1344, 0.6663, 0.3824, 0.4914, 0.1744, 0.6023, 0.3188, 0.6038, 0.3434, 0.5994, 0.1879, 0.4115, 0.3662, 0.6012, 0.3932, 0.6045, 0.195, 0.6579, 0.3887, 0.6377, 0.366, 0.644, 0.3434, 0.6446, 0.3188, 0.6417, 0.3014, 0.6368, 0.3014, 0.6126, 0.1142, 0.385, 0.3679, 0.3361, 0.2479, 0.3987, 0.3789, 0.426, 0.3197, 0.4293, 0.3572, 0.3957, 0.2953, 0.3784, 0.1891, 0.3537, 0.3368, 0.3553, 0.377, 0.6772, 0.3441, 0.6798, 0.3129, 0.6777, 0.2835, 0.6668, 0.2412, 0.6172, 0.103, 0.5312, 0.2676, 0.5754, 0.2979, 0.5668, 0.3334, 0.5661, 0.3664, 0.5739, 0.396, 0.5833, 0.0076, 0.5192, 0.2897, 0.6317, 0.2767, 0.6366, 0.7103, 0.6192, 0.6341, 0.5904, 0.6612, 0.587, 0.6889, 0.5905, 0.7253, 0.6109, 0.6066, 0.5963, 0.6548, 0.656, 0.6299, 0.6539, 0.6807, 0.6527, 0.7021, 0.6464, 0.7522, 0.5892, 0.7199, 0.6244, 0.8937, 0.6, 0.7901, 0.6086, 0.644, 0.4655, 0.7231, 0.4679, 0.6076, 0.6461, 0.6099, 0.3601, 0.986, 0.44, 0.6275, 0.5266, 0.6656, 0.504, 0.711, 0.601, 0.7811, 0.5646, 0.7429, 0.6446, 0.8214, 0.5424, 0.7527, 0.5428, 0.7138, 0.5324, 0.6671, 0.5393, 0.6311, 0.5528, 0.6009, 0.5675, 0.8107, 0.4761, 0.771, 0.651, 0.9805, 0.5984, 0.603, 0.4513, 0.7335, 0.623, 0.9677, 0.3556, 0.8924, 0.4592, 0.8656, 0.6663, 0.6179, 0.4917, 0.8256, 0.6023, 0.6812, 0.6038, 0.6566, 0.5994, 0.8121, 0.4115, 0.6338, 0.6012, 0.6068, 0.6045, 0.805, 0.6579, 0.6113, 0.6377, 0.634, 0.644, 0.6566, 0.6446, 0.6812, 0.6417, 0.6986, 0.6368, 0.6986, 0.6126, 0.8858, 0.385, 0.6319, 0.3365, 0.752, 0.3987, 0.621, 0.426, 0.6802, 0.4293, 0.6428, 0.3957, 0.7047, 0.3785, 0.8109, 0.3537, 0.6632, 0.3554, 0.623, 0.6772, 0.6559, 0.6798, 0.6871, 0.6777, 0.7165, 0.6668, 0.7588, 0.6172, 0.897, 0.5312, 0.7324, 0.5755, 0.7021, 0.5668, 0.6665, 0.5661, 0.6335, 0.5739, 0.6039, 0.5834, 0.9924, 0.5192, 0.7103, 0.6317, 0.7233, 0.6366];
  var CHEEK_TRI = [73, 11, 0, 86, 148, 75, 103, 145, 104, 28, 29, 70, 146, 104, 145, 71, 70, 29, 90, 101, 95, 15, 20, 26, 102, 95, 101, 27, 26, 20, 101, 143, 102, 26, 27, 68, 144, 102, 143, 69, 68, 27, 83, 124, 84, 8, 9, 49, 125, 84, 124, 50, 49, 9, 78, 115, 77, 3, 2, 40, 116, 77, 115, 41, 40, 2, 90, 131, 129, 15, 54, 56, 133, 129, 131, 58, 56, 54, 131, 89, 132, 56, 57, 14, 130, 132, 89, 55, 14, 57, 81, 82, 123, 6, 48, 7, 122, 123, 82, 47, 7, 48, 84, 125, 149, 9, 74, 50, 148, 149, 125, 73, 50, 74, 97, 85, 100, 22, 25, 10, 142, 100, 85, 67, 10, 25, 102, 144, 103, 27, 28, 69, 145, 103, 144, 70, 69, 28, 149, 109, 98, 74, 23, 34, 140, 98, 109, 65, 34, 23, 141, 87, 99, 66, 24, 12, 114, 99, 87, 39, 12, 24, 105, 99, 100, 30, 25, 24, 97, 100, 99, 22, 24, 25, 131, 132, 133, 56, 58, 57, 135, 133, 132, 60, 57, 58, 118, 119, 76, 43, 1, 44, 80, 76, 119, 5, 44, 1, 95, 94, 89, 20, 14, 19, 113, 89, 94, 38, 19, 14, 82, 91, 122, 7, 47, 16, 121, 122, 91, 46, 16, 47, 98, 140, 106, 23, 31, 65, 88, 106, 140, 13, 65, 31, 81, 123, 83, 6, 8, 48, 124, 83, 123, 49, 48, 8, 111, 141, 105, 36, 30, 66, 99, 105, 141, 24, 66, 30, 113, 108, 89, 38, 14, 33, 130, 89, 108, 55, 33, 14, 90, 129, 105, 15, 30, 54, 117, 105, 129, 42, 54, 30, 95, 102, 94, 20, 19, 27, 103, 94, 102, 28, 27, 19, 93, 147, 111, 18, 36, 72, 141, 111, 147, 66, 72, 36, 76, 77, 118, 1, 43, 2, 116, 118, 77, 41, 2, 43, 78, 96, 115, 3, 40, 21, 126, 115, 96, 51, 21, 40, 100, 142, 101, 25, 26, 67, 143, 101, 142, 68, 67, 26, 147, 107, 141, 72, 66, 32, 87, 141, 107, 12, 32, 66, 99, 114, 97, 24, 22, 39, 88, 97, 114, 13, 39, 22, 125, 126, 148, 50, 73, 51, 75, 148, 126, 0, 51, 73, 105, 100, 90, 30, 15, 25, 101, 90, 100, 26, 25, 15, 109, 86, 79, 34, 4, 11, 75, 79, 86, 0, 11, 4, 148, 86, 149, 73, 74, 11, 109, 149, 86, 34, 11, 74, 116, 123, 118, 41, 43, 48, 122, 118, 123, 47, 48, 43, 132, 92, 135, 57, 60, 17, 128, 135, 92, 53, 17, 60, 124, 115, 125, 49, 50, 40, 126, 125, 115, 51, 40, 50, 123, 116, 124, 48, 49, 41, 115, 124, 116, 40, 41, 49, 97, 88, 85, 22, 10, 13, 140, 85, 88, 65, 13, 10, 118, 122, 119, 43, 44, 47, 121, 119, 122, 46, 47, 44, 90, 95, 131, 15, 56, 20, 89, 131, 95, 14, 20, 56, 111, 127, 93, 36, 18, 52, 110, 93, 127, 35, 52, 18, 87, 112, 114, 12, 39, 37, 120, 114, 112, 45, 37, 39, 105, 117, 111, 30, 36, 42, 127, 111, 117, 52, 42, 36, 142, 96, 143, 67, 68, 21, 78, 143, 96, 3, 21, 68, 85, 140, 79, 10, 4, 65, 109, 79, 140, 34, 65, 4, 143, 78, 144, 68, 69, 3, 77, 144, 78, 2, 3, 69, 144, 77, 145, 69, 70, 2, 76, 145, 77, 1, 2, 70, 145, 76, 146, 70, 71, 1, 80, 146, 76, 5, 1, 71, 82, 136, 91, 7, 16, 61, 136, 82, 137, 61, 62, 7, 81, 137, 82, 6, 7, 62, 138, 137, 83, 63, 8, 62, 81, 83, 137, 6, 62, 8, 83, 84, 138, 8, 63, 9, 139, 138, 84, 64, 9, 63, 84, 149, 139, 9, 64, 74, 98, 139, 149, 23, 74, 64, 117, 134, 127, 42, 52, 59, 106, 88, 120, 31, 45, 13, 114, 120, 88, 39, 13, 45, 79, 75, 96, 4, 21, 0, 126, 96, 75, 51, 0, 21, 85, 79, 142, 10, 67, 4, 96, 142, 79, 21, 4, 67];

  var SUB = 3; // Catmull-Rom subdivisions per lip segment (20 pts → 60 pts/ring)
  var RING = LIPS_OUTER.length * SUB;

  // ── Shaders (GLSL ES 1.0 — works on WebGL1 and 2) ─────────────────────────

  var VS_GEOM = [
    'attribute vec2 a_pos;',            // video-pixel coords
    'attribute vec2 a_loc;',            // local ellipse coords (blush only)
    'uniform vec2 u_res;',
    'varying vec2 v_loc;',
    'void main(){',
    '  v_loc = a_loc;',
    '  vec2 c = a_pos / u_res * 2.0 - 1.0;',
    '  gl_Position = vec4(c.x, -c.y, 0.0, 1.0);', // image y-down → clip
    '}'
  ].join('\n');

  var FS_LIP = [
    'precision mediump float;',
    'void main(){ gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0); }'
  ].join('\n');

  // Blush is drawn on the face mesh itself, so the shape lives on the face —
  // it follows the cheekbone, foreshortens when the head turns and moves with
  // expressions. v_loc is the canonical face-map UV (x across, y up; both
  // cheeks share one formula by folding x about the centre line).
  var FS_BLUSH = [
    'precision mediump float;',
    'varying vec2 v_loc;',
    'uniform float u_scale;',           // overall size
    'uniform float u_lift;',            // how far the sweep runs up toward the temple
    'uniform float u_shiftY;',          // move the whole shape up/down
    'uniform float u_soft;',            // edge falloff
    'float hash(vec2 q){ return fract(sin(dot(q, vec2(127.1, 311.7))) * 43758.5453); }',
    'float vnoise(vec2 q){',
    '  vec2 i = floor(q), f = fract(q); f = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);',
    '}',
    'void main(){',
    '  vec2 p = v_loc;',
    '  p.x = min(p.x, 1.0 - p.x);',
    '  p.y -= u_shiftY;',
    '  vec2 A = vec2(0.300, 0.468);',
    '  vec2 T = mix(A, vec2(0.172, 0.552), u_lift);',
    '  vec2 AT = T - A;',
    '  float t = clamp(dot(p - A, AT) / max(dot(AT, AT), 1e-5), 0.0, 1.0);',
    '  vec2 c = A + AT * t;',
    '  float r = mix(0.088, 0.056, t) * u_scale;',
    '  float d = length(p - c) / r;',
    '  float k = clamp(u_soft * 0.6, 0.8, 2.0);',
    '  float stroke = exp(-d * d * k) * mix(1.0, 0.7, t);',
    '  float da = length(p - A) / (0.072 * u_scale);',
    '  float apple = exp(-da * da * k);',
    '  float m = max(stroke, apple);',
    '  m *= 1.0 - smoothstep(0.535, 0.59, p.y) * smoothstep(0.16, 0.24, p.x);',
    '  m *= 1.0 - smoothstep(0.335, 0.395, p.x);',
    '  m *= smoothstep(0.375, 0.44, p.y);',
    '  m *= 0.88 + 0.24 * vnoise(v_loc * 46.0);',
    '  gl_FragColor = vec4(0.0, clamp(m, 0.0, 1.0), 0.0, 1.0);',
    '}'
  ].join('\n');

  var VS_QUAD = [
    'attribute vec2 a_pos;',            // clip-space fullscreen quad
    'varying vec2 v_uv;',
    'void main(){',
    '  v_uv = a_pos * 0.5 + 0.5;',      // v=0 at clip bottom
    '  gl_Position = vec4(a_pos, 0.0, 1.0);',
    '}'
  ].join('\n');

  // Copies scene luminance into the B channel of the mask FBO.
  // The FBO is written y-flipped relative to the video texture (geometry pass
  // flips y), so sample the video flipped here to match.
  var FS_LUMA = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_tex;',
    'void main(){',
    '  vec3 c = texture2D(u_tex, vec2(v_uv.x, 1.0 - v_uv.y)).rgb;',
    '  float l = dot(c, vec3(0.299, 0.587, 0.114));',
    '  gl_FragColor = vec4(0.0, 0.0, l, 1.0);',
    '}'
  ].join('\n');

  // Separable gaussian. R/G (masks) use one spread, B (luma map) its own wider
  // spread so the texture-detail base stays stable even with a tight feather.
  var FS_BLUR = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_tex;',
    'uniform vec2 u_dirM;',             // direction * feather step (masks)
    'uniform vec2 u_dirL;',             // direction * luma step
    'const float w0 = 0.227027; const float w1 = 0.316216; const float w2 = 0.070270;',
    'const float o1 = 1.384615; const float o2 = 3.230769;',
    'void main(){',
    '  vec2 m = texture2D(u_tex, v_uv).rg * w0;',
    '  float l = texture2D(u_tex, v_uv).b * w0;',
    '  m += texture2D(u_tex, v_uv + u_dirM * o1).rg * w1;',
    '  m += texture2D(u_tex, v_uv - u_dirM * o1).rg * w1;',
    '  m += texture2D(u_tex, v_uv + u_dirM * o2).rg * w2;',
    '  m += texture2D(u_tex, v_uv - u_dirM * o2).rg * w2;',
    '  l += texture2D(u_tex, v_uv + u_dirL * o1).b * w1;',
    '  l += texture2D(u_tex, v_uv - u_dirL * o1).b * w1;',
    '  l += texture2D(u_tex, v_uv + u_dirL * o2).b * w2;',
    '  l += texture2D(u_tex, v_uv - u_dirL * o2).b * w2;',
    '  gl_FragColor = vec4(m, l, 1.0);',
    '}'
  ].join('\n');

  // Composite. Makeup is applied as a change of SURFACE COLOUR, not a coat of
  // paint: every pixel keeps the brightness pattern the camera saw (lip lines,
  // the shadow under the upper lip, the light on the lower lip, the room's
  // colour cast) and only the pigment changes. The pigment colour itself is
  // pre-lit on the CPU from what the camera sees on the face this second
  // (see _sceneStats), so it sits in the same light as the skin around it.
  var FS_COMPOSITE = [
    'precision mediump float;',
    'varying vec2 v_uv;',               // v=0 at screen bottom
    'uniform sampler2D u_video;',
    'uniform sampler2D u_mask;',
    'uniform float u_mirror;',
    'uniform float u_divider;',         // compare: makeup only right of this x
    'uniform float u_fade;',            // face-found fade
    'uniform float u_edgeLo; uniform float u_edgeHi;',  // lip mask shaping
    // lip layer
    'uniform float u_lipOn;',
    'uniform vec3  u_lipTarget;',       // pigment colour, lit for this scene (linear)
    'uniform vec3  u_lipMean;',         // the bare lips' average colour (linear)
    'uniform float u_lipCov;',          // how much of the bare lip it covers (0..~0.92)
    'uniform float u_lipSheer;',        // 0 = opaque pigment, 1 = transparent stain
    'uniform float u_lipMatte;',        // how much shine the formula takes off
    'uniform float u_lipTex;',          // how strongly lip lines/creases show through
    'uniform vec2  u_res;',             // video size in px
    'uniform vec2  u_cornerA; uniform vec2 u_cornerB;', // mouth corners (px, image space)
    'uniform float u_cornerR;',         // how far the corner thinning reaches (px)
    'uniform float u_cornerMin;',       // pigment left right at the corner
    // cheek layer
    'uniform float u_blushOn;',
    'uniform vec3  u_blushGain;',       // per-channel colour change of the skin under it
    'uniform float u_blushCov;',
    'uniform float u_skinLum;',         // the face's average skin brightness
    '',
    'vec3 lin(vec3 c){ return c * c; }',
    'vec3 srgb(vec3 c){ return sqrt(max(c, 0.0)); }',
    'float luma(vec3 c){ return dot(c, vec3(0.2126, 0.7152, 0.0722)); }',
    '',
    'void main(){',
    '  vec2 suv = vec2(v_uv.x, 1.0 - v_uv.y);',          // screen uv, y-down
    '  vec2 iuv = suv;',
    '  if (u_mirror > 0.5) iuv.x = 1.0 - iuv.x;',        // image-space uv
    '  vec3 src = texture2D(u_video, iuv).rgb;',
    '  vec3 base = lin(src);',
    '',
    '  vec3 m = texture2D(u_mask, vec2(iuv.x, 1.0 - iuv.y)).rgb;',
    '  float lipM = smoothstep(u_edgeLo, u_edgeHi, m.r);',
    '  float blushM = m.g;',
    '  float on = step(u_divider, suv.x) * u_fade;',
    '',
    '  // ── Lips ─────────────────────────────────────────────────────────',
    '  if (u_lipOn > 0.5 && lipM > 0.001) {',
    '    float lm = max(luma(u_lipMean), 0.002);',
    '    float r = luma(base) / lm;',                     // this pixel vs the average lip
    // Shine: a matte formula takes the top off the highlights but leaves a
    // soft sheen; shadows and lines are left exactly as lit.
    '    float rs = r > 1.0 ? 1.0 + (r - 1.0) * (1.0 - u_lipMatte) : r;',
    // Pigment hides some of the bare lip's own light/dark variation.
    '    float shading = pow(max(rs, 0.0), u_lipTex);',
    '    vec3 opaque = u_lipTarget * shading;',           // even pigment, real lighting
    '    vec3 stain = base * (u_lipTarget / max(u_lipMean, vec3(0.002)));', // see-through dye
    '    vec3 lipCol = mix(opaque, stain, u_lipSheer);',
    // Near the lip line only: a pixel much darker than lip flesh is hair
    // (moustache, beard) or the gap between the lips — pigment does not sit on it.
    '    float inner = smoothstep(0.82, 0.98, m.r);',
    '    float guard = mix(smoothstep(0.28, 0.62, r), 1.0, inner);',
    // Lipstick wears thin into the corners of the mouth.
    '    vec2 px = iuv * u_res;',
    '    float dc = min(distance(px, u_cornerA), distance(px, u_cornerB));',
    '    float corner = mix(u_cornerMin, 1.0, smoothstep(0.0, u_cornerR, dc));',
    '    base = mix(base, lipCol, lipM * u_lipCov * guard * corner * on);',
    '  }',
    '',
    '  // ── Cheeks: the skin\'s own colour, shifted toward the blush ───────',
    '  if (u_blushOn > 0.5 && blushM > 0.001) {',
    '    vec3 tinted = base * u_blushGain;',
    // stubble, a beard line or a glasses frame is much darker than skin — no pigment there
    '    float skinish = smoothstep(0.32, 0.68, luma(base) / max(u_skinLum, 0.002));',
    '    base = mix(base, tinted, blushM * u_blushCov * skinish * on);',
    '  }',
    '',
    '  gl_FragColor = vec4(srgb(base), 1.0);',
    '}'
  ].join('\n');

  // ── Small GL helpers ───────────────────────────────────────────────────────

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error('Shader: ' + gl.getShaderInfoLog(s));
    }
    return s;
  }

  function program(gl, vs, fs) {
    var p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error('Program: ' + gl.getProgramInfoLog(p));
    }
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) {
      var info = gl.getActiveUniform(p, i);
      u[info.name] = gl.getUniformLocation(p, info.name);
    }
    return { prog: p, u: u, a_pos: gl.getAttribLocation(p, 'a_pos'), a_loc: gl.getAttribLocation(p, 'a_loc') };
  }

  function makeFBO(gl, w, h) {
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    var fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { fbo: fbo, tex: tex, w: w, h: h };
  }

  // Closed-loop Catmull-Rom resampling → smooth lip line / crisp cupid's bow.
  function resampleRing(pts, sub, out) {
    var n = pts.length, k = 0;
    for (var i = 0; i < n; i++) {
      var p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
      for (var j = 0; j < sub; j++) {
        var t = j / sub, t2 = t * t, t3 = t2 * t;
        out[k++] = 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
        out[k++] = 0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);
      }
    }
  }

  function hexToRgb01(hex) {
    var h = (hex || '#C62A33').replace('#', '');
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
  }

  // ── Engine ────────────────────────────────────────────────────────────────

  function Engine(canvas) {
    var gl = canvas.getContext('webgl', { alpha: false, antialias: true, preserveDrawingBuffer: true, premultipliedAlpha: true })
          || canvas.getContext('experimental-webgl', { alpha: false, antialias: true, preserveDrawingBuffer: true });
    if (!gl) throw new Error('WebGL is not available');
    this.gl = gl;
    this.canvas = canvas;

    this.pLip = program(gl, VS_GEOM, FS_LIP);
    this.pBlush = program(gl, VS_GEOM, FS_BLUSH);
    this.pLuma = program(gl, VS_QUAD, FS_LUMA);
    this.pBlur = program(gl, VS_QUAD, FS_BLUR);
    this.pComp = program(gl, VS_QUAD, FS_COMPOSITE);

    // Fullscreen quad
    this.quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    // Lip annulus mesh: RING outer + RING inner verts, quads between the rings.
    this.lipVerts = new Float32Array(RING * 4);
    this.lipBuf = gl.createBuffer();
    var idx = [];
    for (var i = 0; i < RING; i++) {
      var o0 = i, o1 = (i + 1) % RING, i0 = RING + i, i1 = RING + (i + 1) % RING;
      idx.push(o0, i0, o1, o1, i0, i1);
    }
    this.lipIdx = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lipIdx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
    this.lipIdxCount = idx.length;

    // Blush: the cheek patch of the face mesh — position (px) + face-map UV.
    this.blushVerts = new Float32Array(CHEEK_LM.length * 4);
    for (var bv = 0; bv < CHEEK_LM.length; bv++) {
      this.blushVerts[bv * 4 + 2] = CHEEK_UV[bv * 2];
      this.blushVerts[bv * 4 + 3] = CHEEK_UV[bv * 2 + 1];
    }
    this.blushBuf = gl.createBuffer();
    this.blushIdx = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.blushIdx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(CHEEK_TRI), gl.STATIC_DRAW);
    this.blushIdxCount = CHEEK_TRI.length;

    this.videoTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    this.fboA = null; this.fboB = null; this.fboC = null;
    this.W = 0; this.H = 0;

    this.source = null;
    this.landmarks = null;
    this.mirror = false;
    this.divider = 0;
    this.fade = 0;

    this.lip = null;     // { color:[r,g,b], intensity }
    this.blush = null;

    // Render tuning (admin-adjustable; defaults tuned on test portraits).
    this.tuning = {
      featherPx: 0.95,   // lip edge feather, fraction of lip height (×0.06)
      dilate: 1.0,       // outer-lip dilation (1.0 = hug MediaPipe lip border; >1 bleeds onto skin/beard)
      matte: 0.7,        // highlight crush (1=dead flat sticker, lower keeps lifelike lip dimension)
      detail: 1.2,       // lip texture / crease pass-through
      edgeLo: 0.12, edgeHi: 0.72,
      blushSize: 1.0, blushSoft: 2.0, blushAspect: 0.58,
      blushShiftX: 0.0, blushShiftY: 0.0, // fractions of cheek-ear distance
      lumaScale: 3.2,    // luma-map blur ÷ mask blur
      // ── v3 realism ──
      lipCovMax: 0.9,    // lipstick at 100%: still lets ~10% of the real lip through
      tintCovMax: 0.85,  // water tint at 100%
      blushCovMax: 0.7,  // blush at 100% — sheer pigment: a strong flush, never paint
      cornerFade: 0.18,  // corner thinning reach, fraction of mouth width
      cornerMin: 0.45,   // pigment left right at the corner
      lipSheer: 0.18,    // lipstick: opaque pigment, a little of the lip's own tone still reads
      tintSheer: 0.72,   // tint: a stain — the lip's own tone shows through
      tintMatte: 0.0,    // tint keeps the lip's natural shine
      lipShade: 0.86,    // lips sit a little darker than a flat swatch (curvature, shadow)
      blushShade: 0.95,
      wbStrength: 0.6,   // how far the pigment follows the room's colour cast
      expoStrength: 0.5, // how far it follows the room's brightness
      skinRef: 0.30      // skin brightness (linear) of a well-lit face
    };

    // Scene measurement, refreshed every few frames from a small copy of the frame.
    this.stats = null;
    this._statsTick = 0;
    this._sc = null; this._sctx = null;

    this._ring = new Float32Array(RING * 2);
    this._raw = [];
    for (var r = 0; r < 20; r++) this._raw.push({ x: 0, y: 0 });
  }

  Engine.prototype.setSource = function (el, w, h) {
    this.source = el;
    if (w !== this.W || h !== this.H) {
      this.W = w; this.H = h;
      this.canvas.width = w; this.canvas.height = h;
      var gl = this.gl, hw = Math.max(2, w >> 1), hh = Math.max(2, h >> 1);
      this.fboA = makeFBO(gl, hw, hh);
      this.fboB = makeFBO(gl, hw, hh);
      this.fboC = makeFBO(gl, hw, hh);
    }
  };

  Engine.prototype.setLandmarks = function (lm) { this.landmarks = lm || null; };
  Engine.prototype.setMirror = function (m) { this.mirror = !!m; };
  Engine.prototype.setDivider = function (x) { this.divider = x || 0; };
  Engine.prototype.setFade = function (f) { this.fade = f; };

  // shade = { color: '#hex' | [r,g,b], intensity: 0..1, finish: 'matte' | 'tint' }
  Engine.prototype.setLip = function (shade) {
    this.lip = shade ? {
      color: Array.isArray(shade.color) ? shade.color : hexToRgb01(shade.color),
      intensity: shade.intensity,
      finish: shade.finish === 'tint' ? 'tint' : 'matte'
    } : null;
  };
  Engine.prototype.setBlush = function (shade) {
    this.blush = shade ? { color: Array.isArray(shade.color) ? shade.color : hexToRgb01(shade.color), intensity: shade.intensity } : null;
  };
  Engine.prototype.setTuning = function (t) {
    for (var k in t) if (Object.prototype.hasOwnProperty.call(this.tuning, k)) this.tuning[k] = t[k];
  };

  // Geometry from landmarks (video-pixel space) ------------------------------

  Engine.prototype._lipGeometry = function (lm) {
    var W = this.W, H = this.H, raw = this._raw, i;

    // Outer ring, dilated slightly from the lip centroid for full coverage.
    var cx = 0, cy = 0;
    for (i = 0; i < 20; i++) { cx += lm[LIPS_OUTER[i]].x; cy += lm[LIPS_OUTER[i]].y; }
    cx /= 20; cy /= 20;
    var g = this.tuning.dilate;
    for (i = 0; i < 20; i++) {
      var p = lm[LIPS_OUTER[i]];
      raw[i].x = (cx + (p.x - cx) * g) * W;
      raw[i].y = (cy + (p.y - cy) * g) * H;
    }
    resampleRing(raw, SUB, this._ring);
    this.lipVerts.set(this._ring, 0);

    for (i = 0; i < 20; i++) { raw[i].x = lm[LIPS_INNER[i]].x * W; raw[i].y = lm[LIPS_INNER[i]].y * H; }
    resampleRing(raw, SUB, this._ring);
    this.lipVerts.set(this._ring, RING * 2);

    // Lip height in px drives the feather + luma-map scale.
    this.lipH = Math.abs(lm[0].y - lm[17].y) * H;
  };

  Engine.prototype._blushGeometry = function (lm) {
    var W = this.W, H = this.H, v = this.blushVerts;
    for (var i = 0; i < CHEEK_LM.length; i++) {
      var p = lm[CHEEK_LM[i]];
      v[i * 4] = p.x * W;
      v[i * 4 + 1] = p.y * H;
    }
  };

  // Scene measurement --------------------------------------------------------
  // Reads the real frame (before any makeup) at a handful of skin and lip
  // points: what colour the light is, how bright the scene is, and what the
  // bare lips look like in it. Smoothed over time so it never flickers.

  Engine.prototype._sceneStats = function (lm) {
    var src = this.source;
    var isVideo = typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement;
    if (isVideo && this.stats && (this._statsTick++ % 6) !== 0) return;

    var sw = Math.min(256, this.W), sh = Math.max(2, Math.round(sw * this.H / this.W));
    if (!this._sc) {
      this._sc = document.createElement('canvas');
      this._sctx = this._sc.getContext('2d', { willReadFrequently: true });
    }
    if (this._sc.width !== sw || this._sc.height !== sh) { this._sc.width = sw; this._sc.height = sh; }
    var data;
    try {
      this._sctx.drawImage(src, 0, 0, sw, sh);
      data = this._sctx.getImageData(0, 0, sw, sh).data;
    } catch (e) { return; }

    var rad = Math.max(1, Math.round(sw * 0.006));
    function at(x, y) {
      var px = Math.round(x * sw), py = Math.round(y * sh), n = 0, r = 0, g = 0, b = 0;
      for (var yy = py - rad; yy <= py + rad; yy++) {
        if (yy < 0 || yy >= sh) continue;
        for (var xx = px - rad; xx <= px + rad; xx++) {
          if (xx < 0 || xx >= sw) continue;
          var o = (yy * sw + xx) * 4, cr = data[o] / 255, cg = data[o + 1] / 255, cb = data[o + 2] / 255;
          r += cr * cr; g += cg * cg; b += cb * cb; n++;   // linear (gamma 2, as the shader)
        }
      }
      return n ? [r / n, g / n, b / n] : null;
    }
    // Average of the points whose brightness is near the median: drops a
    // patch of beard, a stray highlight or hair over the forehead.
    function robust(list) {
      list = list.filter(Boolean);
      if (!list.length) return null;
      var ls = list.map(function (c) { return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; });
      var sorted = ls.slice().sort(function (a, b) { return a - b; });
      var med = sorted[sorted.length >> 1], r = 0, g = 0, b = 0, n = 0;
      for (var i = 0; i < list.length; i++) {
        if (ls[i] < med * 0.7 || ls[i] > med * 1.35) continue;
        r += list[i][0]; g += list[i][1]; b += list[i][2]; n++;
      }
      return n ? [r / n, g / n, b / n] : null;
    }

    var skin = [], lip = [], i;
    for (i = 0; i < SKIN_PTS.length; i++) { var p = lm[SKIN_PTS[i]]; if (p) skin.push(at(p.x, p.y)); }
    for (i = 0; i < LIP_MID.length; i++) {
      var o = lm[LIPS_OUTER[LIP_MID[i]]], q = lm[LIPS_INNER[LIP_MID[i]]];
      if (o && q) lip.push(at((o.x + q.x) / 2, (o.y + q.y) / 2));
    }
    var s = robust(skin), l = robust(lip);
    if (!s || !l) return;

    if (!this.stats || !isVideo) { this.stats = { skin: s, lip: l }; return; }
    var k = 0.25, st = this.stats;
    for (i = 0; i < 3; i++) {
      st.skin[i] += (s[i] - st.skin[i]) * k;
      st.lip[i] += (l[i] - st.lip[i]) * k;
    }
  };

  // The colour the pigment shows in THIS scene: its swatch colour, carried
  // into the room's light (colour cast + brightness, measured off the skin).
  Engine.prototype._litPigment = function (rgb01, shadeMul) {
    var t = this.tuning, st = this.stats;
    var lin = [rgb01[0] * rgb01[0], rgb01[1] * rgb01[1], rgb01[2] * rgb01[2]];
    if (!st) return [lin[0] * shadeMul, lin[1] * shadeMul, lin[2] * shadeMul];
    var L = function (c) { return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; };
    var sk = st.skin, sl = Math.max(L(sk), 0.004), pl = L(SKIN_PRIOR);
    var wb = [0, 0, 0], i;
    for (i = 0; i < 3; i++) {
      var ratio = (sk[i] / sl) / (SKIN_PRIOR[i] / pl);
      wb[i] = Math.max(0.7, Math.min(1.4, 1 + (ratio - 1) * t.wbStrength));
    }
    var wl = L(wb) || 1;
    var expo = Math.max(0.5, Math.min(1.4, Math.pow(sl / t.skinRef, t.expoStrength)));
    var out = [];
    for (i = 0; i < 3; i++) out.push(lin[i] * (wb[i] / wl) * expo * shadeMul);
    return out;
  };

  // Render ------------------------------------------------------------------

  Engine.prototype.render = function () {
    var gl = this.gl;
    if (!this.source || !this.W) return;

    // Upload the current frame.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.source); } catch (e) { return; }

    var lm = this.landmarks;
    var hasFace = !!lm;
    var wantsMakeup = (this.lip || this.blush) && hasFace && this.fade > 0.01;

    if (wantsMakeup) {
      this._sceneStats(lm);
      this._lipGeometry(lm);
      if (this.blush) this._blushGeometry(lm);
      this._maskPass();
      this._blurPass();
    }
    this._compositePass(wantsMakeup);
  };

  Engine.prototype._maskPass = function () {
    var gl = this.gl, fbo = this.fboA;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.fbo);
    gl.viewport(0, 0, fbo.w, fbo.h);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Scene luminance → B
    gl.colorMask(false, false, true, true);
    gl.useProgram(this.pLuma.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pLuma.a_pos);
    gl.vertexAttribPointer(this.pLuma.a_pos, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1i(this.pLuma.u.u_tex, 0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Lip annulus → R
    if (this.lip) {
      gl.colorMask(true, false, false, true);
      gl.useProgram(this.pLip.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.lipBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.lipVerts, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(this.pLip.a_pos);
      gl.vertexAttribPointer(this.pLip.a_pos, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(this.pLip.u.u_res, this.W, this.H);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lipIdx);
      gl.drawElements(gl.TRIANGLES, this.lipIdxCount, gl.UNSIGNED_SHORT, 0);
    }

    // Blush ellipses → G
    if (this.blush) {
      gl.colorMask(false, true, false, true);
      gl.useProgram(this.pBlush.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.blushBuf);
      gl.bufferData(gl.ARRAY_BUFFER, this.blushVerts, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(this.pBlush.a_pos);
      gl.vertexAttribPointer(this.pBlush.a_pos, 2, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(this.pBlush.a_loc);
      gl.vertexAttribPointer(this.pBlush.a_loc, 2, gl.FLOAT, false, 16, 8);
      gl.uniform2f(this.pBlush.u.u_res, this.W, this.H);
      // The admin's existing sliders still steer it: size, softness, and the two
      // shifts (along the cheekbone / up-down), all clamped to sane ranges.
      var bt = this.tuning;
      gl.uniform1f(this.pBlush.u.u_soft, Math.max(1.2, Math.min(3.5, bt.blushSoft)));
      gl.uniform1f(this.pBlush.u.u_scale, Math.max(0.8, Math.min(1.25, bt.blushSize)));
      gl.uniform1f(this.pBlush.u.u_lift, Math.max(0.4, Math.min(1.4, 1 + bt.blushShiftX * 4)));
      gl.uniform1f(this.pBlush.u.u_shiftY, Math.max(-0.05, Math.min(0.05, bt.blushShiftY * 0.2)));
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.blushIdx);
      gl.drawElements(gl.TRIANGLES, this.blushIdxCount, gl.UNSIGNED_SHORT, 0);
      gl.disableVertexAttribArray(this.pBlush.a_loc);
    }

    gl.colorMask(true, true, true, true);
  };

  Engine.prototype._blurPass = function () {
    var gl = this.gl;
    // Feather scales with the on-screen lip size → consistent at any distance.
    var featherFull = Math.max(1.5, Math.min(14, (this.lipH || 40) * 0.055 * this.tuning.featherPx));
    var fm = featherFull * 0.5 / 2.3;       // gaussian step in half-res px
    var fl = fm * this.tuning.lumaScale;

    var passes = [
      { src: this.fboA, dst: this.fboB, dx: 1, dy: 0 },
      { src: this.fboB, dst: this.fboC, dx: 0, dy: 1 }
    ];
    gl.useProgram(this.pBlur.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pBlur.a_pos);
    gl.vertexAttribPointer(this.pBlur.a_pos, 2, gl.FLOAT, false, 0, 0);
    gl.uniform1i(this.pBlur.u.u_tex, 0);
    for (var i = 0; i < 2; i++) {
      var p = passes[i];
      gl.bindFramebuffer(gl.FRAMEBUFFER, p.dst.fbo);
      gl.viewport(0, 0, p.dst.w, p.dst.h);
      gl.bindTexture(gl.TEXTURE_2D, p.src.tex);
      gl.uniform2f(this.pBlur.u.u_dirM, p.dx * fm / p.src.w, p.dy * fm / p.src.h);
      gl.uniform2f(this.pBlur.u.u_dirL, p.dx * fl / p.src.w, p.dy * fl / p.src.h);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  };

  Engine.prototype._compositePass = function (withMakeup) {
    var gl = this.gl, t = this.tuning;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.W, this.H);
    gl.disable(gl.BLEND);
    gl.useProgram(this.pComp.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(this.pComp.a_pos);
    gl.vertexAttribPointer(this.pComp.a_pos, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTex);
    gl.uniform1i(this.pComp.u.u_video, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, (withMakeup ? this.fboC : this.fboA || this.fboC || {}).tex || this.videoTex);
    gl.uniform1i(this.pComp.u.u_mask, 1);
    gl.activeTexture(gl.TEXTURE0);

    gl.uniform1f(this.pComp.u.u_mirror, this.mirror ? 1 : 0);
    gl.uniform1f(this.pComp.u.u_divider, this.divider);
    gl.uniform1f(this.pComp.u.u_fade, withMakeup ? this.fade : 0);

    gl.uniform1f(this.pComp.u.u_edgeLo, t.edgeLo);
    gl.uniform1f(this.pComp.u.u_edgeHi, t.edgeHi);

    var lip = this.lip, bl = this.blush, st = this.stats, U = this.pComp.u;
    var lipOn = !!(lip && st), blOn = !!(bl && st);
    gl.uniform1f(U.u_lipOn, lipOn ? 1 : 0);
    gl.uniform1f(U.u_blushOn, blOn ? 1 : 0);

    if (lipOn) {
      var tint = lip.finish === 'tint';
      var target = this._litPigment(lip.color, t.lipShade);
      gl.uniform3f(U.u_lipTarget, target[0], target[1], target[2]);
      gl.uniform3f(U.u_lipMean, st.lip[0], st.lip[1], st.lip[2]);
      gl.uniform1f(U.u_lipCov, (tint ? t.tintCovMax : t.lipCovMax) * Math.max(0, Math.min(1, lip.intensity)));
      gl.uniform1f(U.u_lipSheer, tint ? t.tintSheer : t.lipSheer);
      // "matte" (0..1) and "detail" are the admin's existing sliders: matte
      // still means how much shine the formula removes; detail how much of the
      // lip's line/crease texture shows through the pigment.
      gl.uniform1f(U.u_lipMatte, tint ? t.tintMatte : Math.max(0, Math.min(0.9, t.matte * 0.6)));
      gl.uniform1f(U.u_lipTex, tint ? 1.0 : Math.max(0.6, Math.min(1.25, 0.7 + 0.25 * t.detail)));
      var lmk = this.landmarks, W = this.W, H = this.H;
      var ax = lmk[61].x * W, ay = lmk[61].y * H, bx = lmk[291].x * W, by = lmk[291].y * H;
      gl.uniform2f(U.u_res, W, H);
      gl.uniform2f(U.u_cornerA, ax, ay);
      gl.uniform2f(U.u_cornerB, bx, by);
      gl.uniform1f(U.u_cornerR, Math.sqrt((bx - ax) * (bx - ax) + (by - ay) * (by - ay)) * t.cornerFade);
      gl.uniform1f(U.u_cornerMin, t.cornerMin);
    }
    if (blOn) {
      var bt = this._litPigment(bl.color, t.blushShade), gain = [];
      for (var c = 0; c < 3; c++) gain.push(Math.max(0.12, Math.min(2.6, bt[c] / Math.max(st.skin[c], 0.004))));
      gl.uniform3f(U.u_blushGain, gain[0], gain[1], gain[2]);
      gl.uniform1f(U.u_blushCov, t.blushCovMax * Math.max(0, Math.min(1, bl.intensity)));
      gl.uniform1f(U.u_skinLum, 0.2126 * st.skin[0] + 0.7152 * st.skin[1] + 0.0722 * st.skin[2]);
    }

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  Engine.prototype.snapshot = function () {
    return this.canvas.toDataURL('image/jpeg', 0.92);
  };

  window.NylaMakeup = window.NylaMakeup || {};
  window.NylaMakeup.Engine = Engine;
  window.NylaMakeup.hexToRgb01 = hexToRgb01;
})();
